import { AccessGate, useCan } from '../../../shared/access/AccessControls';
import { useAllowedMutation } from '../../../shared/access/AccessControls';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Grid, Spacer, Text } from '@chakra-ui/react';
import FormSelect from '../../../shared/components/FormSelect';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import {
  IntegrationBinding,
  IntegrationBindingSummary,
  IntegrationBindingTarget,
} from '../../../shared/types/integrationConfiguration';
import { useSelector } from 'react-redux';
import { selectIntegrationDefinitions } from '../../../store/slices/integrationDefinitionSlice';
import { companiesApi } from '../../../store/services/CompaniesService';
import FormInput from '../../../shared/components/FormInput';

type CustomAttribute = { key: string; value: string };

const IntegrationBindingRow = ({
  companyId = '',
  profileId = '',
  role = undefined,
  configuration = undefined,
}: {
  companyId?: string;
  profileId?: string;
  role?: IntegrationBindingSummary;
  configuration?: IntegrationBinding;
}) => {
  const canUpdate = useCan('ADMIN_ATTACHED_ROLE_UPDATE');
  const canCreate = useCan('ADMIN_ATTACHED_ROLE_CREATE');
  const allRoles = useSelector(selectIntegrationDefinitions);
  const attachedRoleId = role?.id;
  const defaultRoleId = allRoles[0]?.id ?? '';
  // Both company and profile rows use the editor query exclusively. Older
  // summary responses may still include attributes, but are never a fallback.
  const candidate = configuration;
  const candidateAttributes = candidate?.customAttributes;
  const loadedRole =
    candidate?.id === attachedRoleId &&
    candidateAttributes &&
    typeof candidateAttributes === 'object' &&
    !Array.isArray(candidateAttributes) &&
    Object.values(candidateAttributes).every(
      (value) => typeof value === 'string',
    )
      ? candidate
      : undefined;
  const [initializedRole, setInitializedRole] = useState<IntegrationBinding>();

  const [attachIntegrationBinding, { isLoading }] =
    useAllowedMutation(companiesApi.useAttachIntegrationBindingMutation, 'attachIntegrationBinding');

  const [updateIntegrationBinding, { isLoading: isSaving }] =
    useAllowedMutation(companiesApi.useUpdateIntegrationBindingMutation, 'updateIntegrationBinding');
  const canSaveRole =
    canUpdate && !!loadedRole && initializedRole === loadedRole && !isSaving;

  const [deleteIntegrationBinding, { isLoading: isDeleting }] =
    useAllowedMutation(companiesApi.useDetachIntegrationBindingMutation, 'detachIntegrationBinding');

  const rolesOptions = useMemo(() => {
    return allRoles.map(({ id, name }) => ({ value: id, label: name }));
  }, [allRoles]);

  const { register, handleSubmit, reset, control } = useForm<{
    roleId: string;
    customAttributes: CustomAttribute[];
  }>({ defaultValues: { roleId: '', customAttributes: [] } });

  const {
    fields: attributes,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'customAttributes',
  });

  const onSubmit: SubmitHandler<{
    roleId: string;
    customAttributes: CustomAttribute[];
  }> = (args) => {
    if (role) {
      // Guard submission too: disabling the button alone does not protect
      // Enter/programmatic submits before reset has populated the full map.
      if (!canSaveRole || !Array.isArray(args.customAttributes)) return;
      const customAttributes: Record<string, string> = {};
      args.customAttributes.forEach(({ key, value }) => {
        customAttributes[key] = value;
      });
      updateIntegrationBinding({
        attachedRoleId: role.id,
        customAttributes,
      });
    } else {
      if (!canCreate) return;
      const payload: {
        type: IntegrationBindingTarget;
        customAttributes: Record<string, string>;
        roleId: string;
        companyId?: string;
        profileId?: string;
      } = {
        type: (!!companyId && IntegrationBindingTarget.COMPANY) || IntegrationBindingTarget.PROFILE,
        customAttributes: {},
        roleId: args.roleId,
      };

      if (companyId) payload.companyId = companyId;
      else if (profileId) payload.profileId = profileId;

      attachIntegrationBinding(payload);
    }
  };

  useEffect(() => {
    if (attachedRoleId) {
      reset({
        roleId: loadedRole?.role.id ?? '',
        customAttributes: Object.entries(
          loadedRole?.customAttributes ?? {},
        ).map(([key, value]) => ({
          key,
          value,
        })),
      });
      setInitializedRole(loadedRole);
    }
  }, [attachedRoleId, loadedRole, reset]);

  useEffect(() => {
    if (!attachedRoleId) {
      reset({
        roleId: defaultRoleId,
        customAttributes: [],
      });
    }
  }, [attachedRoleId, defaultRoleId, reset]);

  const addAttribute = () => {
    append({ key: '', value: '' });
  };

  const removeAttribute = (index: number) => () => {
    remove(index);
  };

  const deleteRole = () => {
    if (role) deleteIntegrationBinding({ attachedRoleId: role.id });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {(!role && (
        <Grid gap={'1rem'} gridTemplateColumns={'1fr auto'}>
          <FormSelect
            register={register}
            ph={'Integration definition'}
            name={'roleId'}
            labeledOptions={rolesOptions}
          />
          <AccessGate action={role ? "ADMIN_ATTACHED_ROLE_UPDATE" : "ADMIN_ATTACHED_ROLE_CREATE"}><Button
            colorScheme="orange"
            type={'submit'}
            isLoading={isLoading || isSaving}
            w={'max-content'}
          >
            Attach integration
          </Button></AccessGate>
        </Grid>
      )) || (
        <Grid gap={'1rem'}>
          <div>
            <p>
              <b>{role?.role?.name}</b>
            </p>
            <p>{role?.role?.description}</p>

            {!loadedRole && (
              <Text role="status">
                Full configuration is not loaded. Saving is disabled.
              </Text>
            )}

            <fieldset
              disabled={!canSaveRole}
              style={{ border: 0, padding: 0, minWidth: 0 }}
            >
              {!!attributes.length && (
                <Grid gap={'0.5rem'} my={'1rem'}>
                  {/* @ts-ignore */}
                  {attributes.map((attr, index) => (
                    <Grid
                      key={index}
                      gap={'1rem'}
                      gridTemplateColumns={'1fr 1fr auto'}
                    >
                      <FormInput
                        register={register}
                        ph={'Key'}
                        name={`customAttributes.${index}.key`}
                      />
                      <FormInput
                        register={register}
                        ph={'Value'}
                        name={`customAttributes.${index}.value`}
                      />
                      <Button
                        colorScheme="red"
                        type={'button'}
                        w={'max-content'}
                        onClick={removeAttribute(index)}
                      >
                        Delete
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              )}
            </fieldset>
          </div>
          <Flex gap={'0.5rem'}>
            <AccessGate action={role ? "ADMIN_ATTACHED_ROLE_UPDATE" : "ADMIN_ATTACHED_ROLE_CREATE"}><Button
              colorScheme="orange"
              type={'submit'}
              isLoading={isLoading || isSaving}
              isDisabled={!canSaveRole}
              w={'max-content'}
              size={'sm'}
            >
              Save binding
            </Button></AccessGate>
            <Button
              size={'sm'}
              colorScheme="gray"
              type={'button'}
              w={'max-content'}
              onClick={addAttribute}
              isDisabled={!canSaveRole}
            >
              Add attribute
            </Button>
            <Spacer />
            <AccessGate action="ADMIN_ATTACHED_ROLE_DELETE"><Button
              colorScheme="red"
              type={'button'}
              isLoading={isDeleting}
              w={'max-content'}
              size={'sm'}
              onClick={deleteRole}
            >
              Delete binding
            </Button></AccessGate>
          </Flex>
        </Grid>
      )}
    </form>
  );
};

export default IntegrationBindingRow;
